/**
 * RowBreakdown — lead row count summary table shown on delivery cards.
 *
 * Props:
 *   counts — { total, total_rejected?, total_pending_review?, zb_invalid?,
 *              by_goal: [{ goal_id, goal_name, component_name, count, rejected?, pending_review? }] }
 *
 *   status — the delivery's own status ("Pending" / "Delivered to PM"), used to decide
 *            whether the "all leads rejected" notice is still relevant
 *   showReason — show the upload job's own (technical) reason text; PMs/admins only
 *
 * counts.issue ({code, level, message, reason?}, from the backend's delivery_issues.py) is
 * rendered as a plain-language banner: a delivery that created no leads, whose every lead
 * was rejected, or that a corrected re-upload replaced.
 *
 * rejected/pending_review (per-goal and total) are only present once a delivery
 * has gone through NeTRA Lead ID auto-ingestion (see backend CLAUDE.md) — older
 * deliveries or any other counts shape without them just don't render those
 * columns, so this stays backward compatible with every existing caller.
 */
import { issueVisible, issueStyle } from '../utils/deliveryNotices';

export default function RowBreakdown({ counts, status, showReason = false }) {
  if (!counts) return <span style={{ color: '#aaa', fontSize: '12px' }}>No row data</span>;
  const { total, by_goal, total_rejected, total_pending_review } = counts;
  const hasStatusCounts = by_goal?.some(g => g.rejected !== undefined || g.pending_review !== undefined);
  const thStyle = { padding: '4px 8px', textAlign: 'left', color: '#777', fontWeight: '600', borderBottom: '1px solid #e0e0e0' };
  const issue = counts.issue;
  const issueStyleNow = issueVisible(issue, status) ? issueStyle(issue) : null;
  return (
    <div>
      {issueStyleNow && (
        <div role="status" style={{ marginBottom: '8px', padding: '7px 10px', borderRadius: '4px', fontSize: '12px', lineHeight: 1.4,
          background: issueStyleNow.background, color: issueStyleNow.color, border: `1px solid ${issueStyleNow.border}` }}>
          {issue.message}
          {showReason && issue.reason && (
            <div style={{ marginTop: '4px', fontSize: '11px', opacity: 0.85 }}>Reason recorded at upload: {issue.reason}</div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: by_goal?.length ? '6px' : 0 }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50' }}>
          {total} row{total !== 1 ? 's' : ''}
        </div>
        {hasStatusCounts && (
          <>
            <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '10px', background: '#fdedec', color: '#c0392b', fontWeight: '600' }}>
              Rejected: {total_rejected || 0}
            </span>
            <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '10px', background: '#fef9e7', color: '#b7770d', fontWeight: '600' }}>
              Pending Review: {total_pending_review || 0}
            </span>
          </>
        )}
      </div>
      {by_goal?.length > 0 && (
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '11px' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={thStyle}>Component</th>
              <th style={thStyle}>Goal</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Rows</th>
              {hasStatusCounts && <th style={{ ...thStyle, textAlign: 'right' }}>Rejected</th>}
              {hasStatusCounts && <th style={{ ...thStyle, textAlign: 'right' }}>Pending Review</th>}
            </tr>
          </thead>
          <tbody>
            {by_goal.map(g => (
              <tr key={g.goal_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '3px 8px', color: '#555', fontSize: '11px' }}>{g.component_name}</td>
                <td style={{ padding: '3px 8px', color: '#2c3e50', fontSize: '11px' }}>{g.goal_name}</td>
                <td style={{ padding: '3px 8px', textAlign: 'right', fontWeight: '600', fontSize: '11px', color: g.count > 0 ? '#2c3e50' : '#aaa' }}>{g.count}</td>
                {hasStatusCounts && (
                  <td style={{ padding: '3px 8px', textAlign: 'right', fontSize: '11px', color: g.rejected > 0 ? '#c0392b' : '#aaa' }}>{g.rejected ?? '—'}</td>
                )}
                {hasStatusCounts && (
                  <td style={{ padding: '3px 8px', textAlign: 'right', fontSize: '11px', color: g.pending_review > 0 ? '#b7770d' : '#aaa' }}>{g.pending_review ?? '—'}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
